import { EventLargeCard } from "@components/cards/EventLargeCard"
import { ContentBlocks } from "eddev/blocks"
import { AwardSeasonPageFragment } from "../../../types.graphql"
import { AwardStatus } from "./AwardStatus"
import { StartAwardsSubmission } from "./StartAwardsSubmission"
import { EventHeroCard } from "@components/cards/EventHeroCard"
import { useEffect, useMemo, useRef, useState } from "react"
import { ResponsiveImage } from "@components/atoms/ResponsiveImage"
import { AnimatePresence, motion } from "framer-motion"
import { StaggerTransitionRoot } from "@components/motion/Transition"

type Props = {
  award: AwardSeasonPageFragment
}

export function AwardSeasonLandingPage(props: Props) {
  const info = props.award?.info!
  const labelRef = useRef<HTMLDivElement>(null!)

  return (
    <StaggerTransitionRoot>
      <div className="grid-auto">
        <div className="col-span-12 min-h-[110vw] md:min-h-[min(80vh,60vw)] items-center justify-center flex relative">
          <AwardSpread entries={props.award.randomEntries} labelRef={labelRef} />
          {/* Status */}
          <div ref={labelRef}>
            <div className="pt-8 pb-8 px-6">
              <AwardStatus year={info.awardYear!} statusTitle={props.award?.statusText} status={info.status!} subtitle={props.award?.subtitle} editing isLandingPage={true} />
            </div>
          </div>
        </div>

        {/* Start Submission */}
        <StartAwardsSubmission />

        {info.eventAnnouncement || props.award.contentBlocks.length > 0 ? (
          <div className="col-span-12 grid-auto border-t pt-10">
            {/* Event */}
            {info.eventAnnouncement && (
              <div className="col-span-12 md:col-2-8 mb-6">
                <EventHeroCard {...info.eventAnnouncement} ctaLabel="Purchase Tickets" />
              </div>
            )}

            {/* CTA cards */}
            <div className="col-span-12 md:col-2-8 mb-8 grid grid-cols-2 gap-2">
              <ContentBlocks blocks={props.award?.contentBlocks} />
            </div>
          </div>
        ) : null}
      </div>
    </StaggerTransitionRoot>
  )
}

type Rect = {
  x: number
  y: number
  w: number
  h: number
}

type Piece = Rect & { id: string; srcSet: string; aspect: number }

function AwardSpread({ entries, labelRef }: { entries: AwardSeasonPageFragment["randomEntries"]; labelRef: React.RefObject<HTMLDivElement> }) {
  const ref = useRef<HTMLDivElement>(null)
  const [size, setSize] = useState({ aspect: 0, width: 0, height: 0 })
  const [labelRect, setLabelRect] = useState<Rect>({ x: 0, y: 0, w: 0, h: 0 })

  // observe container
  useEffect(() => {
    if (!ref.current) return
    const ro = new ResizeObserver(() => {
      // The callback can fire after unmount, when the ref has been cleared
      const b = ref.current?.getBoundingClientRect()
      if (!b || !b.height) return
      const aspect = b.width / b.height
      setSize((cur) => (Math.abs(cur.aspect - aspect) > 0.2 ? { aspect, width: b.width, height: b.height } : cur))
    })
    ro.observe(ref.current)
    return () => ro.disconnect()
  }, [])

  // observe label
  useEffect(() => {
    if (!ref.current || !labelRef.current) return
    const update = () => {
      // The callback can fire after unmount, when the refs have been cleared
      const c = ref.current?.getBoundingClientRect()
      const l = labelRef.current?.getBoundingClientRect()
      if (!c || !l) return
      setLabelRect({ x: l.left - c.left, y: l.top - c.top, w: l.width, h: l.height })
    }
    const ro = new ResizeObserver(update)
    ro.observe(labelRef.current)
    update() // initial
    return () => ro.disconnect()
  }, [labelRef])

  const [pieces, setPieces] = useState<Piece[]>([])

  const iterate = useMemo(() => {
    const pad = 16 // optional safety margin
    const label = {
      x: labelRect.x - pad,
      y: labelRect.y - pad,
      w: labelRect.w + pad * 2,
      h: labelRect.h + pad * 2,
    }

    let count = 0

    const rectsOverlap = (a: Rect, b: Rect) => !(a.x + a.w <= b.x || a.x >= b.x + b.w || a.y + a.h <= b.y || a.y >= b.y + b.h)

    let itemSize = size.width * 0.18
    let availableSize = (size.height - label.h) * 0.45
    if (size.height > size.width) {
      itemSize = availableSize * 0.8
    } else {
      itemSize = Math.min(itemSize, availableSize)
    }

    setTimeout(() => setPieces([]), 0) // clear pieces to allow re-adding on size change

    function iterate() {
      setPieces((result) => {
        if (!entries?.length) return []
        const choices = entries?.filter((e) => !result.some((r) => r.id === e?.id!) && e?.artworks?.length! > 0).sort(() => Math.random() - 0.5) // shuffle
        count++
        for (let i = 0; i < count; i++) {
          choices?.push(choices.shift()!)
        }
        for (const e of choices!) {
          const base = { id: e!.id!, srcSet: e!.artworks![0]!.srcSet!, aspect: e!.artworks![0]!.aspect!, x: 0, y: 0, w: itemSize, h: itemSize }
          for (let i = 0; i < 30; i++) {
            const candidate = {
              ...base,
              w: itemSize,
              h: itemSize / base.aspect,
              x: Math.random() * (size.width - itemSize),
              y: Math.random() * (size.height - itemSize),
            }
            const overlaps = rectsOverlap(candidate, label) || result.some((r) => rectsOverlap(candidate, r))
            if (!overlaps) {
              result.push(candidate)
              break
            }
          }
        }

        return result.slice(0, 7)
      })
    }

    return iterate
  }, [entries, size])

  useEffect(() => {
    if (entries?.length === 0) return
    setTimeout(() => iterate(), 10)
    const interval1 = setInterval(() => {
      setPieces((result) => {
        result.shift()
        return [...result]
      })
    }, 2000)
    const interval2 = setInterval(() => {
      iterate()
    }, 3000)
    return () => {
      clearInterval(interval1)
      clearInterval(interval2)
    }
  }, [iterate])

  useEffect(() => {
    const ro = new ResizeObserver(() => {
      const bounds = ref.current?.getBoundingClientRect()
      if (!bounds || !bounds.height) return

      const aspect = bounds.width / bounds.height

      setSize((cur) => {
        if (Math.abs(cur.aspect - aspect) > 0.2) {
          return { aspect, width: bounds.width, height: bounds.height }
        } else {
          return cur
        }
      })
    })

    if (ref.current) ro.observe(ref.current)
    return () => ro.disconnect()
  }, [])

  return (
    <div className="absolute pointer-events-none overflow-hidden inset-0" ref={ref}>
      {pieces.length > 0 && (
        <AnimatePresence initial={false}>
          {pieces?.map((item, i) => {
            return (
              <motion.div
                key={item.id}
                className="w-[--w] h-[--h] top-[--y] left-[--x] absolute"
                initial={{ y: 40, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 40, opacity: 0 }}
                transition={{ duration: 0.9, ease: "easeInOut" }}
                style={
                  {
                    "--w": (item.w / size.width) * 100 + "%",
                    "--h": (item.h / size.height) * 100 + "%",
                    "--x": (item.x / size.width) * 100 + "%",
                    "--y": (item.y / size.height) * 100 + "%",
                  } as any
                }
              >
                <ResponsiveImage fadeOnLoad srcSet={item.srcSet} aspect={item.aspect} max objectFit="contain" objectPosition="center" />
              </motion.div>
            )
          })}
        </AnimatePresence>
      )}
    </div>
  )
}
